const STORAGE_KEY = 'smartinventory_ad_session_v2';

export const AD_POLICY = {
  initialDelayMs: 5000,
  minIntervalMs: 60 * 1000,
  maxImpressionsPerSession: 4,
  dismissedCooldownMs: 2 * 60 * 1000,
  clickedCooldownMs: 3 * 60 * 1000,
  sessionTtlMs: 4 * 60 * 60 * 1000,
};

function safeNow() {
  return Date.now();
}

function defaultSessionData() {
  return {
    sessionStartedAt: safeNow(),
    adDismissedAt: 0,
    adLastShownAt: 0,
    adLastClickedAt: 0,
    adClicksCount: 0,
    adImpressionsCount: 0,
    aggressiveDismissals: 0,
    adsDisabled: false,
  };
}

export function getSessionData() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultSessionData();
    const merged = { ...defaultSessionData(), ...JSON.parse(raw) };
    if (safeNow() - Number(merged.sessionStartedAt || 0) > AD_POLICY.sessionTtlMs) {
      const reset = defaultSessionData();
      saveSessionData(reset);
      return reset;
    }
    return merged;
  } catch {
    return defaultSessionData();
  }
}

export function saveSessionData(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // No-op when storage is unavailable.
  }
}

export function getSessionDuration(sessionData) {
  const startedAt = Number(sessionData?.sessionStartedAt || 0);
  if (!startedAt) return 0;
  return Math.max(0, safeNow() - startedAt);
}

export function shouldShowAds({ user, subscriptionStatus, sessionData }) {
  const hasAuthoritativeStatus = typeof subscriptionStatus === 'string' && subscriptionStatus.length > 0;
  const subscriptionActive = hasAuthoritativeStatus
    ? subscriptionStatus === 'ACTIVE'
    : Boolean(user?.subscriptionActive);

  if (subscriptionActive) return false;
  if (sessionData?.adsDisabled) return false;
  if ((sessionData?.adImpressionsCount || 0) >= AD_POLICY.maxImpressionsPerSession) return false;

  const now = safeNow();
  const sessionStartedAt = Number(sessionData?.sessionStartedAt || now);
  if (now - sessionStartedAt < AD_POLICY.initialDelayMs) return false;

  const lastShownAt = Number(sessionData?.adLastShownAt || 0);
  if (now - lastShownAt < AD_POLICY.minIntervalMs) return false;

  const lastDismissedAt = Number(sessionData?.adDismissedAt || 0);
  if (now - lastDismissedAt < AD_POLICY.dismissedCooldownMs) return false;

  const lastClickedAt = Number(sessionData?.adLastClickedAt || 0);
  if (now - lastClickedAt < AD_POLICY.clickedCooldownMs) return false;

  // If user keeps dismissing ads, reduce frequency progressively.
  const dismissals = Number(sessionData?.aggressiveDismissals || 0);
  const adaptiveCooldownMs = Math.min(8 * 60 * 1000, dismissals * 60 * 1000);
  if (adaptiveCooldownMs > 0 && now - lastDismissedAt < adaptiveCooldownMs) return false;

  return true;
}

export function getNextEligibleAdTime(sessionData) {
  const now = safeNow();
  const candidates = [
    Number(sessionData?.sessionStartedAt || now) + AD_POLICY.initialDelayMs,
    Number(sessionData?.adLastShownAt || 0) + AD_POLICY.minIntervalMs,
    Number(sessionData?.adDismissedAt || 0) + AD_POLICY.dismissedCooldownMs,
    Number(sessionData?.adLastClickedAt || 0) + AD_POLICY.clickedCooldownMs,
  ];
  return Math.max(now, ...candidates);
}

export function markAdShown(sessionData) {
  const next = {
    ...sessionData,
    adLastShownAt: safeNow(),
    adImpressionsCount: Number(sessionData?.adImpressionsCount || 0) + 1,
  };
  saveSessionData(next);
  return next;
}

export function markAdClicked(sessionData) {
  const next = {
    ...sessionData,
    adLastClickedAt: safeNow(),
    adClicksCount: Number(sessionData?.adClicksCount || 0) + 1,
  };
  saveSessionData(next);
  return next;
}

export function markAdDismissed(sessionData) {
  const now = safeNow();
  const recentShown = now - Number(sessionData?.adLastShownAt || 0) < 15000;
  const next = {
    ...sessionData,
    adDismissedAt: now,
    aggressiveDismissals: Number(sessionData?.aggressiveDismissals || 0) + (recentShown ? 1 : 0),
  };
  saveSessionData(next);
  return next;
}

export function resetAdSessionData() {
  const next = defaultSessionData();
  saveSessionData(next);
  return next;
}
